import { useRef, useState } from 'react';
import { Box, chakra, Flex, Grid, Heading, Input, Text, Textarea } from '@chakra-ui/react';
import { SectionLabel, PillButton } from '@denise-augusta/ui';
import { useSiteContent } from '@/providers/SiteContentProvider';
import { SiteFooter, NAV_HEIGHT } from '@/layouts/PublicLayout';
import { submitContactForm, type ContactFormValues } from '@/lib/contact-api';

const PROMISES = [
  {
    title: 'No Spam',
    body: 'Your information stays private. No mailing lists, no marketing.',
    icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  },
  {
    title: 'No Obligation',
    body: 'Reaching out is just a conversation — zero pressure.',
    icon: (
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    ),
  },
  {
    title: 'Quick Start',
    body: "Once access is provided, I'm ready to begin.",
    icon: (
      <>
        <polyline points="5 12 12 5 19 12" />
        <polyline points="5 19 12 12 19 19" />
      </>
    ),
  },
];

const fieldStyles = {
  px: '1rem',
  py: '0.88rem',
  bg: 'paper.warm',
  border: '1px solid',
  borderColor: 'paper.border',
  borderBottomColor: 'brand.pale',
  fontFamily: 'body',
  fontSize: '0.9rem',
  color: 'ink.body',
  borderRadius: '8px',
  _focus: {
    bg: 'paper.white',
    borderColor: 'brand.pale',
    borderBottomColor: 'brand.mid',
    boxShadow: '0 2px 0 #3D52A8',
    outline: 'none',
  },
  _placeholder: { color: 'ink.light' },
} as const;

/** Server-side validation errors are re-shown against the field they name. */
function invalidStyles(message?: string) {
  return message
    ? { borderColor: 'brand.terracotta', borderBottomColor: 'brand.terracotta' }
    : {};
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <Text fontSize="0.72rem" color="brand.terracotta">
      {message}
    </Text>
  );
}

function Label({ children, htmlFor }: { children: React.ReactNode; htmlFor: string }) {
  return (
    <Box
      as="label"
      // @ts-expect-error htmlFor passes through chakra
      htmlFor={htmlFor}
      fontSize="0.66rem"
      fontWeight="600"
      letterSpacing="0.17em"
      textTransform="uppercase"
      color="brand.ink"
    >
      {children}
    </Box>
  );
}

type SubmitState = 'idle' | 'sending' | 'sent' | 'error';

export function Contact() {
  const content = useSiteContent();
  const [state, setState] = useState<SubmitState>('idle');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [errorText, setErrorText] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  // How long the form was on screen before it was submitted — one half of
  // the bot check on the server (the hidden field below is the other).
  const renderedAt = useRef(Date.now());

  const submitted = state === 'sending' || state === 'sent';

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitted) return;

    const data = new FormData(event.currentTarget);
    const values: ContactFormValues = {
      firstName: String(data.get('fname') ?? ''),
      lastName: String(data.get('lname') ?? ''),
      email: String(data.get('email') ?? ''),
      phone: String(data.get('phone') ?? ''),
      company: String(data.get('company') ?? ''),
      message: String(data.get('message') ?? ''),
      website: String(data.get('website') ?? ''),
      elapsedMs: Date.now() - renderedAt.current,
    };

    setState('sending');
    setFieldErrors({});
    setErrorText('');

    const result = await submitContactForm(values);
    if (result.ok) {
      setState('sent');
      // After a few seconds: clear the form and re-enable the fields.
      setTimeout(() => {
        formRef.current?.reset();
        renderedAt.current = Date.now();
        setState('idle');
      }, 5000);
      return;
    }

    setState('error');
    setFieldErrors(result.fieldErrors ?? {});
    setErrorText(result.message);
  };

  return (
    <Box
      css={{
        backgroundColor: '#F5F4F1',
        backgroundImage:
          "linear-gradient(rgba(245,244,241,0.25), rgba(245,244,241,0.25)), url('/images/contact-bg.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Grid pt={NAV_HEIGHT} templateColumns={{ base: '1fr', md: '42% 1fr' }} minH={{ md: '100vh' }}>
        {/* Info panel */}
        <Flex
          bg="rgba(234,237,245,0.38)"
          backdropFilter="blur(4px)"
          px={{ base: '1.8rem', md: '4rem' }}
          pl={{ md: '5rem' }}
          py={{ base: '5rem', md: '5.5rem' }}
          direction="column"
          justify="center"
          position="relative"
          overflow="hidden"
          borderRight={{ md: '2.5px solid' }}
          borderBottom={{ base: '1px solid', md: 'none' }}
          borderColor={{ base: 'brand.pale', md: 'brand.lime' }}
        >
          <SectionLabel>Let's Talk</SectionLabel>
          <Heading
            as="h1"
            fontFamily="heading"
            fontSize="clamp(2.8rem, 4.5vw, 4.2rem)"
            fontWeight="300"
            lineHeight="1"
            color="brand.ink"
            mb="1.4rem"
          >
            Ready to hand
            <br />
            off the{' '}
            <Box as="em" fontStyle="italic" color="brand.mid">
              chaos?
            </Box>
          </Heading>
          <Text
            fontFamily="heading"
            fontStyle="italic"
            fontSize="clamp(1.4rem, 2vw, 1.8rem)"
            fontWeight="300"
            lineHeight="1.4"
            color="brand.mid"
            mb="3.5rem"
            pl="1.2rem"
            borderLeft="2px solid"
            borderLeftColor="brand.lime"
            maxW="420px"
          >
            {content.contact.quote}
          </Text>
        </Flex>

        {/* Form panel */}
        <Flex
          bg="rgba(255,255,255,0.45)"
          backdropFilter="blur(4px)"
          px={{ base: '1.8rem', md: '4rem' }}
          py={{ base: '3rem', md: '5.5rem' }}
          align="flex-start"
        >
          <chakra.form ref={formRef} onSubmit={handleSubmit} display="flex" flexDirection="column" gap="1.2rem" w="100%">
            {/* Honeypot: invisible and skipped by keyboard, so only a bot
                fills it in. Submissions carrying it are dropped server-side. */}
            <Box position="absolute" left="-9999px" w="1px" h="1px" overflow="hidden" aria-hidden="true">
              <label htmlFor="website">
                Leave this field empty
                <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
              </label>
            </Box>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="1.2rem">
              <Flex direction="column" gap="0.42rem">
                <Label htmlFor="fname">First Name</Label>
                <Input id="fname" name="fname" placeholder="Jane" required disabled={submitted} {...fieldStyles} {...invalidStyles(fieldErrors.firstName)} />
                <FieldError message={fieldErrors.firstName} />
              </Flex>
              <Flex direction="column" gap="0.42rem">
                <Label htmlFor="lname">Last Name</Label>
                <Input id="lname" name="lname" placeholder="Smith" required disabled={submitted} {...fieldStyles} {...invalidStyles(fieldErrors.lastName)} />
                <FieldError message={fieldErrors.lastName} />
              </Flex>
            </Grid>
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="1.2rem">
              <Flex direction="column" gap="0.42rem">
                <Label htmlFor="email">Email Address</Label>
                <Input id="email" name="email" type="email" placeholder="jane@company.com" required disabled={submitted} {...fieldStyles} {...invalidStyles(fieldErrors.email)} />
                <FieldError message={fieldErrors.email} />
              </Flex>
              <Flex direction="column" gap="0.42rem">
                <Label htmlFor="phone">Phone (Optional)</Label>
                <Input id="phone" name="phone" type="tel" autoComplete="tel" placeholder="(555) 010-9987" disabled={submitted} {...fieldStyles} {...invalidStyles(fieldErrors.phone)} />
                <FieldError message={fieldErrors.phone} />
              </Flex>
            </Grid>
            <Flex direction="column" gap="0.42rem">
              <Label htmlFor="company">Company / Organization (Optional)</Label>
              <Input id="company" name="company" placeholder="Acme Corp" disabled={submitted} {...fieldStyles} {...invalidStyles(fieldErrors.company)} />
              <FieldError message={fieldErrors.company} />
            </Flex>
            <Flex direction="column" gap="0.42rem">
              <Label htmlFor="message">Tell Me About Your Needs</Label>
              <Textarea
                id="message"
                name="message"
                placeholder="A bit about your situation and what you'd like help with…"
                minH="130px"
                resize="vertical"
                disabled={submitted}
                {...fieldStyles}
                {...invalidStyles(fieldErrors.message)}
              />
              <FieldError message={fieldErrors.message} />
            </Flex>

            {state === 'error' && (
              <Box
                role="alert"
                bg="#FBEDEB"
                border="1px solid"
                borderColor="brand.terracotta"
                px="1.4rem"
                py="1.1rem"
                fontSize="0.87rem"
                color="brand.terracotta"
                borderRadius="12px"
              >
                {errorText}
                {/* Only offer the direct address when one is actually
                    published — otherwise "email us" points nowhere. */}
                {content.contact.email && (
                  <>
                    {' '}You can also write to{' '}
                    <chakra.a
                      href={`mailto:${content.contact.email}`}
                      color="brand.terracotta"
                      textDecoration="underline"
                      fontWeight="600"
                    >
                      {content.contact.email}
                    </chakra.a>
                    .
                  </>
                )}
              </Box>
            )}

            {state !== 'sent' ? (
              <PillButton asChild scheme="plain" px="2.8rem" py="1rem" alignSelf="flex-start">
                <button type="submit" disabled={state === 'sending'}>
                  {state === 'sending' ? 'Sending…' : 'Send Message →'}
                </button>
              </PillButton>
            ) : (
              <Box
                role="status"
                bg="brand.wash"
                border="1px solid"
                borderColor="brand.pale"
                px="1.4rem"
                py="1.1rem"
                fontSize="0.87rem"
                color="brand.ink"
                borderRadius="12px"
              >
                ✓ &nbsp;Message sent! I'll be in touch soon.
              </Box>
            )}
          </chakra.form>
        </Flex>
      </Grid>

      {/* PROMISE STRIP */}
      <Grid
        bg="rgba(237,236,234,0.4)"
        backdropFilter="blur(4px)"
        borderTop="1px solid"
        borderTopColor="paper.border"
        px={{ base: '1.8rem', md: '3.5rem' }}
        py={{ base: '2.5rem', md: '3rem' }}
        templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)' }}
        gap="2rem"
      >
        {PROMISES.map((promise) => (
          <Flex key={promise.title} align="flex-start" gap="1rem">
            <Flex
              w="38px"
              h="38px"
              flexShrink="0"
              bg="brand.wash"
              border="1px solid"
              borderColor="brand.pale"
              align="center"
              justify="center"
              borderRadius="10px"
              color="brand.mid"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="16" height="16">
                {promise.icon}
              </svg>
            </Flex>
            <Box>
              <Text fontSize="0.76rem" fontWeight="600" letterSpacing="0.1em" textTransform="uppercase" color="brand.ink" mb="0.25rem">
                {promise.title}
              </Text>
              <Text fontSize="0.78rem" color="ink.soft" lineHeight="1.65">
                {promise.body}
              </Text>
            </Box>
          </Flex>
        ))}
      </Grid>

      <SiteFooter copyright={content.footer.copyright} variant="glass" />
    </Box>
  );
}
